import { useEffect, useRef } from "react";
import { ref, onDisconnect, set, serverTimestamp } from "firebase/database";
import { database } from "src/initialiseFirebase";
import type { PlayerId } from "src/core/types";
import type { DatabaseReference } from "firebase/database";

/**
 * Hook to track player presence and connection status
 * Automatically updates Firebase when player connects/disconnects
 */
export const usePlayerPresence = (gameId: string | null, playerId: PlayerId | null): null => {
  const presenceRef = useRef<DatabaseReference | null>(null);

  useEffect(() => {
    if (!gameId || !playerId) {
      return;
    }

    // Reference to this player's presence in Firebase
    const playerPresenceRef = ref(database, `games/${gameId}/players/${playerId}`);
    presenceRef.current = playerPresenceRef;

    // Set player as connected
    const updatePresence = async () => {
      try {
        await set(playerPresenceRef, {
          playerId,
          connected: true,
          lastSeen: serverTimestamp()
        });

        // Set up disconnect handler
        const disconnectRef = onDisconnect(playerPresenceRef);
        await disconnectRef.update({
          connected: false,
          lastSeen: serverTimestamp()
        });
      } catch (error) {
        console.error("Error setting up presence:", error);
      }
    };

    updatePresence();

    // Update lastSeen every 30 seconds to show activity
    const heartbeatInterval = setInterval(async () => {
      try {
        await set(ref(database, `games/${gameId}/players/${playerId}/lastSeen`), serverTimestamp());
      } catch (error) {
        console.error("Error updating heartbeat:", error);
      }
    }, 30000);

    return () => {
      clearInterval(heartbeatInterval);
      // Mark as disconnected when component unmounts
      if (presenceRef.current) {
        set(ref(database, `games/${gameId}/players/${playerId}/connected`), false);
      }
    };
  }, [gameId, playerId]);

  return null;
};
